import 'twin.macro';
import React from 'react';
import { getContrastBasedOnIndex } from 'utils/notationSystems/getContrastBasedOnIndex';
import { css } from 'twin.macro';
import useIsMobile from '@teetsh/app/src/components/ResponsiveSwitcher/useIsMobile';

interface Handle {
  id: string;
  value: number;
  label: string;
}

interface Props {
  handles: Handle[];
}

export function RangeVisualization({ handles }: Props) {
  const isMobile = useIsMobile();

  // Filter valid handles and sort by value
  const validHandles = handles
    .filter(h => h.value >= 0 && h.value !== undefined)
    .sort((a, b) => a.value - b.value);

  // Calculate position for each label (middle of range)
  const getLabelPosition = (index: number): number => {
    const currentValue = validHandles[index]?.value || 0;
    const previousValue = index > 0 ? validHandles[index - 1]?.value : 0;
    return (previousValue + currentValue) / 2;
  };

  // Generate ticks: every 20 points on mobile (0, 20, 40, 60, 80, 100), every 5 points on desktop
  const tickInterval = isMobile ? 20 : 5;
  const tickCount = isMobile ? 6 : 21;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const value = i * tickInterval;
    return {
      value,
      percent: value,
    };
  });

  return (
    <div tw="relative w-full h-10 select-none">
      {/* Labels Section - positioned above the bar like in MultiSlider */}
      <div>
        {validHandles.map((handle, index) => {
          const position = getLabelPosition(index);
          return (
            <div
              key={handle.id}
              tw="text-sm absolute mt-[5px] text-center -translate-x-1/2"
              style={{
                left: `${position}%`,
              }}
            >
              <div
                tw="whitespace-nowrap"
                css={css`
                  color: ${getContrastBasedOnIndex(index, validHandles.length)};
                `}
              >
                {handle.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Colored Bar Section - matching MultiSlider track style */}
      <div tw="absolute w-full mt-[26px] h-[6px] bg-neutral-quiet">
        {validHandles.map((handle, index) => {
          const previousValue = index > 0 ? validHandles[index - 1]?.value : 0;
          const width = handle.value - previousValue;

          return (
            <div
              key={handle.id}
              tw="absolute h-full"
              style={{
                left: `${previousValue}%`,
                width: `${width}%`,
              }}
              css={css`
                background-color: ${getContrastBasedOnIndex(
                  index,
                  validHandles.length,
                )};
              `}
            />
          );
        })}
      </div>

      {/* Ticks Section - matching MultiSlider tick style */}
      <div>
        {ticks.map((tick, index) => (
          <div key={index}>
            <div
              tw="absolute mt-9 -ml-[0.5px] w-[1px] h-2 bg-neutral-quiet"
              style={{
                left: `${tick.percent}%`,
              }}
            />
            <div
              tw="absolute mt-[45px] text-[10px] text-center"
              style={{
                marginLeft: `${-(100 / tickCount) / 2}%`,
                width: `${100 / tickCount}%`,
                left: `${tick.percent}%`,
              }}
            >
              {tick.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
